import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import Helper from '../../../../Common/Helper';
import { PagedResponse } from "@iconics/core";
import { Ontology, OntologyNode } from './DataModels';
import EditOntologyNodeDialog from './EditOntologyNodeDialog';
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from '@serendie/symbols';
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Switch } from '@serendie/ui';
import { Validator } from '@iconics/core';
import { Guid, TypeHelper } from '@iconics/core';
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextField } from '@iconics/components';

class EditOntology extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private languageValuesHelper: LanguageValuesHelper;

    private userCanCreate: boolean;
    private userCanUpdate: boolean;
    private userCanDelete: boolean;
    private userCanEnable: boolean;
    private userCanDisable: boolean;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            id: '',
            name: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            isEnabled: false,
            concurrencyToken: '',
            nameError: '',
            descriptionError: '',
            ontologyNodesGridData: { rows: [], checkedIds: [] },
            ontologyNodesGridRefreshToken: Guid.new().toString(),
            showEditNodeDialog: false,
            selectedOntologyNode: null,
            sections: [],
        };

        this.userCanCreate = this.local.hasRight('DATAMODEL.Ontologies.Create');
        this.userCanUpdate = this.local.hasRight('DATAMODEL.Ontologies.Update');
        this.userCanDelete = this.local.hasRight('DATAMODEL.Ontologies.Delete');
        this.userCanEnable = this.local.hasRight('API.Ontologies.Enable');
        this.userCanDisable = this.local.hasRight('API.Ontologies.Disable');
    }

    private get isNew(): boolean
    {
        return (!this.props.params.id);
    }

    private get selectedOntologyNodeIds(): string[]
    {
        return this.state.ontologyNodesGridData.checkedIds ?? [];
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Ontology_PageTitle;
        this.backUrl = '/admin/ontologies';
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();
        if (!this.isNew)
        {
            await this.loadOntology();
        }
    }

    private async loadOntology(): Promise<void>
    {
        await this.loading(async () =>
        {
            const ontologyData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.get<Ontology>({
                id: this.props.params.id ?? '',
                tenancy: appContext().tenantId,
                select: Ontology
            });

            await this.setStateAsync({
                id: ontologyData.Id,
                name: ontologyData.Name,
                displayName: this.languageValuesHelper.fromString(ontologyData.DisplayName),
                description: ontologyData.Description,
                isEnabled: TypeHelper.toBoolean(ontologyData.IsEnabled) ?? false,
                concurrencyToken: ontologyData["@odata.etag"]
            });
        });
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.delete({
                tenancy: appContext().tenantId,
                id: this.state.id,
            });
            this.props.navigate("/admin/ontologies")
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (this.isNew)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.post({
                    tenancy: appContext().tenantId,
                    body: {
                        Name: this.state.name,
                        Description: this.state.description,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                    }
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.put<Ontology>({
                    tenancy: appContext().tenantId,
                    id: this.state.id,
                    body: {
                        Name: this.state.name,
                        Description: this.state.description,
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        '@odata.etag': this.state.concurrencyToken,
                    }
                });
            }
            this.props.navigate("/admin/ontologies")
        });
    }

    private async handleOntologyNodesRequest(request: IGridRowsRequest<IOntologyNode>): Promise<IGridRowsResponse<IOntologyNode>>
    {
        if (!this.props.params.id)
        {
            return { rows: [], nextSkipToken: '' };
        }
        const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<OntologyNode[]>>({
            tenancy: appContext().tenantId,
            id: this.props.params.id,
        });

        const ontologyNodes = response.value.map(x => ({
            id: x.Id,
            name: x.Name,
            displayName: this.languageValuesHelper.fromString(x.DisplayName),
            isEnabled: TypeHelper.toBoolean(x.IsEnabled) ?? false,
            isRecursive: TypeHelper.toBoolean(x.IsRecursive) ?? false,
            isRequired: TypeHelper.toBoolean(x.IsRequired) ?? false,
            parentId: x.Parent_ID,
            parentName: response.value.find(y => y.Id === x.Parent_ID)?.Name ?? '',
            etag: x['@odata.etag']
        }));

        const sortedRows = DataGrid.sort(ontologyNodes, request.sorter);
        return { rows: sortedRows, nextSkipToken: '' };
    }

    private handleSaveOntologyNode(): void
    {
        this.setState({
            showEditNodeDialog: false,
            ontologyNodesGridRefreshToken: Guid.new().toString(),
        });
    }

    private async handleEnableOntologyNodes(): Promise<void>
    {
        await this.loading(async () =>
        {
            let index = 0;
            while (index < this.selectedOntologyNodeIds.length)
            {
                const i = index;
                const node = this.state.ontologyNodesGridData.rows.filter(x => x.id === this.selectedOntologyNodeIds[i])[0];
                if (!node.isEnabled)
                {
                    await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.patch({
                        tenancy: appContext().tenantId,
                        id: this.state.id,
                        ontologynodeid: node.id,
                        body: {
                            IsEnabled: 'Yes',
                            "@odata.etag": node.etag
                        }
                    });
                }
                index = index + 1;
            }
            this.setState({ ontologyNodesGridRefreshToken: Guid.new().toString() });
        });
    }

    private async handleDisableOntologyNodes(): Promise<void>
    {
        await this.loading(async () =>
        {
            let index = 0;
            while (index < this.selectedOntologyNodeIds.length)
            {
                const i = index;
                const node = this.state.ontologyNodesGridData.rows.filter(x => x.id === this.selectedOntologyNodeIds[i])[0];
                if (node.isEnabled)
                {
                    await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.patch({
                        tenancy: appContext().tenantId,
                        id: this.state.id,
                        ontologynodeid: node.id,
                        body: {
                            IsEnabled: 'No',
                            "@odata.etag": node.etag
                        }
                    });
                }
                index = index + 1;
            }
            this.setState({ ontologyNodesGridRefreshToken: Guid.new().toString() });
        });
    }

    private handleEditOntologyNode(id: string): React.ReactNode
    {
        if (!this.userCanUpdate)
        {
            return;
        }

        const ontologyNode = this.state.ontologyNodesGridData.rows.find(i => i.id === id);
        if (!ontologyNode)
        {
            return;
        }

        this.setState({
            showEditNodeDialog: true,
            selectedOntologyNode: {
                id: ontologyNode.id,
                name: ontologyNode.name,
                displayName: ontologyNode.displayName,
                isEnabled: ontologyNode.isEnabled,
                isRequired: ontologyNode.isRequired,
                isRecursive: ontologyNode.isRecursive,
                parentId: ontologyNode.parentId,
                parentName: ontologyNode.parentName,
                etag: ontologyNode.etag,
            }
        });
    }

    private async handleDeleteOntologyNodes(): Promise<void>
    {
        await this.loading(async () =>
        {
            let index = 0;
            while (index < this.selectedOntologyNodeIds.length)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.delete({
                    tenancy: appContext().tenantId,
                    id: this.state.id,
                    ontologynodeid: this.selectedOntologyNodeIds[index],
                });
                index = index + 1;
            }
            this.setState({ ontologyNodesGridRefreshToken: Guid.new().toString() });
        });
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <GridContainer spacing={5}>
                            <GridItem>
                                <Card
                                    icon={<SerendieSymbol name='loader' />}
                                    title={this.labels.Platform_Ontology_DetailsTitle}
                                    subTitle={this.labels.Platform_Ontology_DetailsSubTitle}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                                >
                                    <GridContainer>
                                        <GridItem expanded={6}>
                                            <TextField
                                                required
                                                label={this.labels.Ontology_Name}
                                                placeholder={this.labels.Ontology_Name}
                                                value={this.state.name}
                                                onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                                invalid={this.state.nameError !== ''}
                                                invalidMessage={this.state.nameError}
                                            />
                                        </GridItem>
                                        <GridItem expanded={6}>
                                            <MultilingualTextField
                                                label={this.labels.Ontology_DisplayName}
                                                placeholder={this.labels.Ontology_DisplayName}
                                                value={this.state.displayName}
                                                onChange={value => this.setState({ displayName: value })}
                                                localisation={appContext().localisation}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <TextField
                                                required
                                                label={this.labels.Ontology_Description}
                                                placeholder={this.labels.Ontology_Description}
                                                value={this.state.description}
                                                onChange={e => this.setState({ description: e.target.value }, () => this.validator.validate('description'))}
                                                invalid={this.state.descriptionError !== ''}
                                                invalidMessage={this.state.descriptionError}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <Switch
                                                label={this.labels.Ontology_IsEnabled}
                                                checked={this.state.isEnabled}
                                                onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </Card>
                            </GridItem>
                            {
                                !this.isNew &&
                                <GridItem>
                                    <Card
                                        title={this.labels.Platform_Ontology_NodeListTitle}
                                        subTitle={this.labels.Platform_Ontology_NodeListSubTitle}
                                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                                    >
                                        <GridContainer>
                                            <GridItem>
                                                <ButtonBar>
                                                    <RightButtons>
                                                        <DeleteButton
                                                            small={true}
                                                            disabled={!this.userCanDelete || this.selectedOntologyNodeIds.length < 1}
                                                            onDelete={() => this.handleDeleteOntologyNodes()}
                                                            dialogMessage={this.labels.Platform_OntologyNode_ConfirmDeleteMsg}
                                                        />
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='close' />}
                                                            onClick={() => this.handleDisableOntologyNodes()}
                                                            disabled={!this.userCanDisable || this.selectedOntologyNodeIds.length < 1}
                                                        >
                                                            {this.labels.Platform_CommonAction_Disable}
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='check' />}
                                                            onClick={() => this.handleEnableOntologyNodes()}
                                                            disabled={!this.userCanEnable || this.selectedOntologyNodeIds.length < 1}
                                                        >
                                                            {this.labels.Platform_CommonAction_Enable}
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='pencil' />}
                                                            onClick={() => this.handleEditOntologyNode(this.selectedOntologyNodeIds[0])}
                                                            disabled={!this.userCanUpdate || this.selectedOntologyNodeIds.length !== 1}
                                                        >
                                                            {this.labels.Platform_CommonAction_Edit}
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='plus' />}
                                                            onClick={() => this.setState({ showEditNodeDialog: true, selectedOntologyNode: null })}
                                                            disabled={!this.userCanCreate || this.selectedOntologyNodeIds.length > 0}
                                                        >
                                                            {this.labels.Platform_CommonAction_Add}
                                                        </Button>
                                                    </RightButtons>
                                                </ButtonBar>
                                            </GridItem>
                                            <GridItem>
                                                <DataGrid
                                                    columns={[
                                                        {
                                                            columnId: Helper.nameOf<IOntologyNode>("name"),
                                                            title: this.labels.OntologyNode_Name,
                                                            getCellValue: i => i.name,
                                                            columnWidth: 20,
                                                            sortable: true,
                                                        },
                                                        {
                                                            columnId: Helper.nameOf<IOntologyNode>("isEnabled"),
                                                            title: this.labels.OntologyNode_IsEnabled,
                                                            getCellValue: i => <SerendieSymbol name={i.isEnabled ? 'check-circle' : 'close-circle'} style={{ marginLeft: '32px' }} />,
                                                            columnWidth: 20,
                                                            sortable: true,
                                                        },
                                                        {
                                                            columnId: Helper.nameOf<IOntologyNode>("isRecursive"),
                                                            title: this.labels.OntologyNode_IsRecursive,
                                                            getCellValue: i => <SerendieSymbol name={i.isRecursive ? 'check-circle' : 'close-circle'} style={{ marginLeft: '32px' }} />,
                                                            columnWidth: 20,
                                                            sortable: true,
                                                        },
                                                        {
                                                            columnId: Helper.nameOf<IOntologyNode>("isRequired"),
                                                            title: this.labels.OntologyNode_IsRequired,
                                                            getCellValue: i => <SerendieSymbol name={i.isRequired ? 'check-circle' : 'close-circle'} style={{ marginLeft: '32px' }} />,
                                                            columnWidth: 20,
                                                            sortable: true,
                                                        },
                                                        {
                                                            columnId: Helper.nameOf<IOntologyNode>("parentName"),
                                                            title: this.labels.OntologyNode_Parent,
                                                            getCellValue: i => i.parentName,
                                                            columnWidth: 20,
                                                            sortable: true,
                                                        },
                                                    ]}
                                                    data={this.state.ontologyNodesGridData}
                                                    rowId={i => i.id}
                                                    onChange={i => this.setState({ ontologyNodesGridData: i })}
                                                    onRowsRequest={i => this.handleOntologyNodesRequest(i)}
                                                    onCellClicked={(row, column) => this.handleEditOntologyNode(row.id)}
                                                    showCheckBoxes={true}
                                                    allowOnlyOneCheckBox={false}
                                                    refreshToken={this.state.ontologyNodesGridRefreshToken}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </Card>
                                </GridItem>
                            }
                        </GridContainer>
                    </LayoutContent>
                    <LayoutRight>
                        <PageNav
                            sections={this.state.sections}
                        />
                    </LayoutRight>
                    <LayoutFooter>
                        <ButtonBar>
                            <LeftButtons>
                                {
                                    !this.isNew &&
                                    <DeleteButton
                                        disabled={!this.userCanDelete}
                                        dialogMessage={this.labels.Platform_Ontology_ConfirmDeleteMsg}
                                        onDelete={() => this.handleDelete()}
                                    />
                                }
                            </LeftButtons>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.navigate("/admin/ontologies")}
                                >
                                    {this.labels.Platform_CommonAction_Cancel
                                    }
                                </Button>
                                <Button
                                    styleType='filled'
                                    disabled={!this.validator.isValid}
                                    onClick={() => this.handleSave()}
                                >
                                    {this.labels.Platform_CommonAction_Save
                                    }
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </LayoutFooter>
                </LayoutContainer>
                <EditOntologyNodeDialog
                    open={this.state.showEditNodeDialog}
                    onClose={() => this.setState({ showEditNodeDialog: false, selectedOntologyNode: null })}
                    selectedOntologyNode={this.state.selectedOntologyNode}
                    onSave={() => this.handleSaveOntologyNode()}
                    tenancy={appContext().tenantId}
                    ontologyId={this.state.id}
                />
            </>
        )
    }
}

export default withRouting(EditOntology);

export interface IProps extends IRouterProps<IQueryParams>
{
}

export interface IState
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    description: string;
    isEnabled: boolean;
    concurrencyToken: string;
    nameError: string;
    descriptionError: string;
    ontologyNodesGridData: IGridData<IOntologyNode>;
    ontologyNodesGridRefreshToken: string;
    showEditNodeDialog: boolean;
    selectedOntologyNode: IOntologyNode | null,
    sections: IPageNavSection[];
}

export interface IQueryParams
{
    id: string | undefined;
}

export interface IOntologyNode
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    isEnabled: boolean;
    isRecursive: boolean;
    isRequired: boolean;
    parentId: string;
    parentName: string;
    etag: string;
}
